import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Bot, Brain, Cpu, Network, Cloud, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';
import './Services.css';

const servicesData = [
  {
    id: 'agentic-ai',
    title: 'Agentic AI Solutions',
    icon: Bot,
    description: 'Autonomous AI agents that can reason, plan, and execute complex tasks with minimal human intervention.',
    features: [
      'Intelligent autonomous decision-making systems',
      'Multi-agent collaboration frameworks',
      'Goal-oriented task execution',
      'Self-learning and adaptive behaviors',
      'Enterprise workflow automation'
    ]
  },
  {
    id: 'llm-slm',
    title: 'Large & Small Language Models',
    icon: Brain,
    description: 'Custom LLM and SLM implementations for natural language understanding, generation, and domain-specific applications.',
    features: [
      'Custom fine-tuned language models',
      'Domain-specific SLM development',
      'Conversational AI and chatbots',
      'Text generation and summarization',
      'Multilingual NLP solutions'
    ]
  },
  {
    id: 'machine-learning',
    title: 'Machine Learning & AI',
    icon: Cpu,
    description: 'End-to-end machine learning solutions from model development to production deployment.',
    features: [
      'Predictive analytics and forecasting',
      'Computer vision and image processing',
      'Anomaly detection systems',
      'Recommendation engines',
      'MLOps and model lifecycle management'
    ]
  },
  {
    id: 'iot-edge',
    title: 'IoT & Edge Computing',
    icon: Network,
    description: 'Connected devices and edge AI solutions for real-time data processing and intelligent automation.',
    features: [
      'Sensor integration and data collection',
      'Real-time monitoring and alerts',
      'Edge AI inference deployment',
      'LiDAR and advanced sensing technologies',
      'Smart device orchestration'
    ]
  },
  {
    id: 'cloud-platforms',
    title: 'Cloud-Native Platforms',
    icon: Cloud,
    description: 'Scalable cloud infrastructure and microservices architecture for modern enterprise applications.',
    features: [
      'Microservices and API development',
      'Containerization and orchestration',
      'Serverless computing solutions',
      'Multi-cloud deployment strategies',
      'DevOps and CI/CD pipelines'
    ]
  },
  {
    id: 'enterprise-software',
    title: 'Enterprise Software Development',
    icon: Database,
    description: 'Custom enterprise applications including CRM, ERP, POS, and industry-specific platforms.',
    features: [
      'Web and mobile application development',
      'Database design and optimization',
      'Third-party system integrations',
      'Legacy system modernization',
      'Custom business logic implementation'
    ]
  }
];

const sectionVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const contentVariants = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.45, ease: 'easeOut' } },
  exit: { opacity: 0, x: -30, transition: { duration: 0.25 } },
};

const Services = () => {
  const [activeTab, setActiveTab] = useState('agentic-ai');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: tabsRef, isVisible: tabsVisible } = useScrollAnimation({ threshold: 0.1 });

  const activeService = servicesData.find((s) => s.id === activeTab);

  return (
    <section id="services" className="services-section">
      <div className="services-container">
        <motion.div
          ref={headerRef}
          className="services-header"
          variants={sectionVariants}
          initial="hidden"
          animate={headerVisible ? 'visible' : 'hidden'}
        >
          <div className="section-label">What We Do</div>
          <h2 className="display-large">Our <span className="gradient-text">Services</span></h2>
          <p className="body-medium">Comprehensive technology solutions tailored to your industry needs</p>
        </motion.div>

        <motion.div
          ref={tabsRef}
          variants={sectionVariants}
          initial="hidden"
          animate={tabsVisible ? 'visible' : 'hidden'}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="services-tabs">
            <TabsList className="tabs-list">
              {servicesData.map((service, i) => {
                const Icon = service.icon;
                return (
                  <TabsTrigger key={service.id} value={service.id} className="tab-trigger">
                    <Icon size={20} />
                    <span className="tab-label">{service.title}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <AnimatePresence mode="wait">
              {activeService && (
                <motion.div
                  key={activeService.id}
                  variants={contentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <TabsContent value={activeService.id} className="tab-content" forceMount>
                    <div className="service-card">
                      <motion.div
                        className="service-icon"
                        initial={{ scale: 0.8, rotate: -10 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                      >
                        {React.createElement(activeService.icon, { size: 40 })}
                      </motion.div>
                      <h3 className="heading-1">{activeService.title}</h3>
                      <p className="body-medium service-description">{activeService.description}</p>
                      <div className="service-features">
                        <h4 className="heading-3">Key Features:</h4>
                        <ul className="features-list">
                          {activeService.features.map((feature, index) => (
                            <motion.li
                              key={feature}
                              className="body-small"
                              initial={{ opacity: 0, x: -15 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.08, duration: 0.35 }}
                            >
                              {feature}
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </TabsContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;